<template>
  <div
    class="flex flex-col gap-4 bg-white rounded-xl border-[1.5px] border-primary30 p-5"
    data-testid="handover-flow-strip"
  >
    <div
      v-if="isExpired"
      data-testid="handover-expired-warning"
      class="flex items-center gap-2 rounded p-2 bg-[#fef9e7] border border-[#f1c21b]"
    >
      <v-icon size="16" :icon="mdiAlert" color="#f1c21b" class="shrink-0" />
      <span class="text-sm text-[#16161d] leading-[18px]">
        Este convite expirou. Não é mais possível aprovar ou recusar candidatos.
      </span>
    </div>

    <div class="flex flex-col lg:flex-row lg:items-stretch gap-5">
      <!-- Passou ⟶ pretende assumir: grupo coeso, sem esticar no restante da faixa -->
      <div class="flex items-center gap-6 min-w-0" data-testid="flow-strip-transfer-group">
        <div class="flex flex-col gap-2 min-w-0">
          <span
            class="text-sm font-semibold text-[#7f7f8e] text-left"
            data-testid="flow-strip-requester-label"
          >
            Profissional que passou
          </span>
          <DoctorInfo
            :name="requester.name"
            :subtitle="`CRM ${requester.crm}`"
            :avatar-url="requester.avatarUrl"
            :nps-score="requester.npsScore"
            size="md"
          />
        </div>

        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="shrink-0 w-6 h-6 text-primary30"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path :d="mdiArrowRight" fill="currentColor" />
        </svg>

        <div class="flex flex-col gap-2 min-w-0">
          <template v-if="candidate">
            <span
              class="text-sm font-semibold text-[#7f7f8e] text-left"
              data-testid="flow-strip-candidate-label"
            >
              Profissional que pretende assumir
            </span>
            <DoctorInfo
              :name="candidate.doctor?.name ?? '—'"
              :subtitle="candidate.doctor?.crm ? `CRM ${candidate.doctor.crm}` : ''"
              :avatar-url="candidate.doctor?.avatarUrl ?? null"
              :nps-score="candidate.doctor?.npsScore ?? null"
              size="md"
            />
          </template>
          <span
            v-else
            class="text-sm text-left text-[#7f7f8e]"
            data-testid="flow-strip-empty-candidate"
          >
            Nenhum candidato na fila
          </span>
        </div>
      </div>

      <!-- Empurra o restante (chip, badge de fila, ações) para a direita sem esticar o grupo acima -->
      <div
        class="hidden lg:block w-px self-stretch bg-[#e8e8ed] shrink-0 lg:ml-auto"
        data-testid="flow-strip-transfer-group-divider"
      />

      <!-- Situação do candidato na fila -->
      <div
        v-if="formattedAcceptedAt || message"
        class="flex flex-col justify-center gap-2 lg:w-[260px] shrink-0"
      >
        <div
          v-if="formattedAcceptedAt"
          class="flex items-center gap-1.5 bg-[#f5f5f7] rounded-[6px] px-3.5 py-2.5 text-xs"
          data-testid="flow-strip-accepted-chip"
        >
          <span class="text-[#52526f]">Aceitou em</span>
          <span class="font-bold text-[#16161d]">{{ formattedAcceptedAt }}</span>
        </div>

        <div
          v-if="message"
          class="flex items-center gap-2 rounded p-2 bg-[#fef9e7] border border-[#f1c21b]"
          data-testid="flow-strip-queue-badge"
        >
          <v-icon size="16" :icon="mdiClockOutline" color="#f1c21b" class="shrink-0" />
          <span class="text-sm text-[#16161d] leading-[18px]">{{ message }}</span>
        </div>
      </div>

      <div
        v-if="candidate || totalInvites > 0"
        class="hidden lg:block w-px self-stretch bg-[#e8e8ed] shrink-0"
      />

      <!-- Ações -->
      <div
        v-if="candidate || totalInvites > 0"
        class="flex flex-col justify-center gap-2.5 lg:w-[260px] shrink-0"
      >
        <template v-if="candidate">
          <FilledButton
            text="Aprovar para este médico"
            width="100%"
            height="48px"
            data-testid="approve-handover-button"
            :disabled="isProcessing || isExpired"
            class="bg-primary30"
            @click="$emit('approve')"
          />
          <OutlineButton
            text="Recusar troca"
            width="100%"
            height="48px"
            data-testid="decline-handover-button"
            :disabled="isProcessing || isExpired"
            @click="$emit('decline')"
          />
        </template>

        <button
          v-if="totalInvites > 0"
          class="flex items-center justify-center gap-2 p-1 text-sm font-bold text-primary30"
          data-testid="view-all-candidates-button"
          @click="$emit('viewAllCandidates')"
        >
          <v-icon size="20" :icon="mdiArrowRight" color="#2F7F91" />
          Ver todos os candidatos
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { format, parseISO } from 'date-fns'
import { mdiAlert, mdiArrowRight, mdiClockOutline } from '@mdi/js'
import type {
  DoctorSummary,
  InviteResponseV2
} from '@/Network/Types/Responses/HandoverInviteResponses'
import DoctorInfo from '@/components/DoctorInfo.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'

const props = withDefaults(
  defineProps<{
    requester: DoctorSummary
    candidate: InviteResponseV2 | null
    message: string
    totalInvites: number
    isExpired?: boolean
    isProcessing?: boolean
  }>(),
  { isExpired: false, isProcessing: false }
)

defineEmits<{
  approve: []
  decline: []
  viewAllCandidates: []
}>()

const formattedAcceptedAt = computed(() => {
  if (!props.candidate?.acceptedAt) return ''
  try {
    return format(parseISO(props.candidate.acceptedAt), "dd/MM/yyyy 'às' HH:mm")
  } catch {
    return props.candidate.acceptedAt
  }
})
</script>
